import * as _ from 'lodash';
import { createSelector } from 'reselect';

import { getFilter, getGrouping, getSearch, getSort } from '../issuesV2/selectors';
import type { AnySort, Category, Grouping } from '../../../shared/issuesV2';
import type { FilterForCategory, SavedFilter } from '../../../shared/savedFilters';
import type { IssuesV2Store } from '../issuesV2/types';
import type { ReduxStore } from '../store';

import type { SavedFiltersStore } from './types';

export const getSavedFiltersStore = (state: ReduxStore): SavedFiltersStore => state.savedFilters;
const createSavedFiltersSelector = <R>(selectorFn: (state: SavedFiltersStore) => R) =>
  createSelector(getSavedFiltersStore, selectorFn);

//  asyncStatus is divided into sub-fields for each of the various network requests.
export const getAsyncStatus = createSavedFiltersSelector((state: SavedFiltersStore) => state.asyncStatus);
export const getGetFiltersAsyncStatus = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.getFilters);
export const getSaveOrDeleteAsyncStatus = createSelector(
  getAsyncStatus,
  (asyncStatus) => asyncStatus.saveOrDeleteFilter,
);

export const getFilters = createSavedFiltersSelector((state: SavedFiltersStore) => state.filters);

export const getFiltersByCategory = createSelector(
  [
    getFilters,
    //  Forward the extra arg (Category) to the selector. Allows passing in from the component.
    (_state, category: Category) => category,
  ],
  (filters, category): SavedFilter[] => filters[category],
);

export const getCurrentFilter = createSavedFiltersSelector((state: SavedFiltersStore) => state.currentFilter);

//  Finds the current filter in one of the filter arrays, depending on the category provided.
export const getCurrentFilterByCategory = createSelector(
  [
    getFilters,
    getCurrentFilter,
    //  Forward the extra arg (Category) to the selector. Allows passing in from the component.
    (_state, category: Category) => category,
  ],
  (filters, currentFilter, category): SavedFilter | null => {
    if (!currentFilter || !filters?.[category]) {
      return null;
    }
    return filters[category].find((filter) => filter.id === currentFilter) || null;
  },
);

//  From IssuesV2, this takes the filter + search.
//  Used for determining what the current filters state is.
export const getCurrentFilterState = createSelector(
  [getFilter, getSearch],
  (filter: IssuesV2Store<Category>['filter'], search: string): FilterForCategory => ({
    ...filter,
    search,
  }),
);

//  Returns true if there is a currentFilter selected but the user has changed it in some way.
export const getCanSave = createSelector(
  [getCurrentFilterState, getSort, getGrouping, getCurrentFilterByCategory],
  (filter: FilterForCategory, sort: AnySort, group: Grouping, currentFilter: SavedFilter<Category> | null): boolean => {
    // if filter is different, for every key in filter, if that key does not exist in currentFilter.filter and the key's value is empty, then it is not different
    const isFilterDifferent =
      currentFilter &&
      Object.keys(filter).some((key) => {
        const filterKey = key as keyof FilterForCategory;
        const doesValueMatch = _.isEqual(filter[filterKey], currentFilter.filter[filterKey]);
        const isNewValueEmpty = _.isEmpty(filter[filterKey]);
        const didOldValueExist = currentFilter.filter[filterKey] !== undefined;
        // If the value is empty, and the old value did not exist, then it is not different.
        const isNotEmpty = !(isNewValueEmpty && !didOldValueExist);
        return !doesValueMatch && isNotEmpty;
      });

    const isSortDifferent = currentFilter && sort !== currentFilter.sort;
    const isGroupDifferent = currentFilter && group !== currentFilter.group;
    const doesCurrentChoicesDifferFromFilter = !!(isFilterDifferent || isSortDifferent || isGroupDifferent);
    const hasNoPreviousChoice = !currentFilter;
    const doesCurrentChoiceExist = !!(filter || sort || group);
    return doesCurrentChoicesDifferFromFilter || (hasNoPreviousChoice && doesCurrentChoiceExist);
  },
);

export const getHasChanged = createSavedFiltersSelector((state: SavedFiltersStore) => state.hasChanged);
